import React, { useEffect, useRef, useState } from "react";

type Props = {
  latitude: number | null;
  longitude: number | null;
  country?: string | null;
  onPick: (latitude: number, longitude: number) => void;
};

type PlaceResult = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
};

const OSM_RASTER_STYLE = {
  version: 8 as const,
  sources: {
    osm: {
      type: "raster" as const,
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [
    {
      id: "osm",
      type: "raster" as const,
      source: "osm",
    },
  ],
};

const FRANCE_CENTRE: [number, number] = [2.2137, 46.2276];
const UK_CENTRE: [number, number] = [-1.5, 52.4];

function isCoordinate(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

function roundCoordinate(value: number) {
  return Math.round(value * 1_000_000) / 1_000_000;
}

function countryCode(country?: string | null) {
  const normalised = (country ?? "").trim().toLowerCase();
  if (normalised.includes("france")) return "fr";
  if (
    normalised.includes("england") ||
    normalised.includes("scotland") ||
    normalised.includes("wales") ||
    normalised.includes("united kingdom") ||
    normalised === "uk"
  ) {
    return "gb";
  }
  return null;
}

export function LakeCentreMapPicker({
  latitude,
  longitude,
  country,
  onPick,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<import("maplibre-gl").Map | null>(null);
  const markerRef = useRef<import("maplibre-gl").Marker | null>(null);
  const onPickRef = useRef(onPick);
  const latestLocationRef = useRef({ latitude, longitude, country });
  const searchCacheRef = useRef(new Map<string, PlaceResult[]>());
  const lastSearchAtRef = useRef(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [placeQuery, setPlaceQuery] = useState("");
  const [placeResults, setPlaceResults] = useState<PlaceResult[]>([]);
  const [searchingPlace, setSearchingPlace] = useState(false);
  const [placeSearchError, setPlaceSearchError] = useState<string | null>(null);

  latestLocationRef.current = { latitude, longitude, country };

  useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let cancelled = false;

    async function initialiseMap() {
      try {
        const maplibre = await import("maplibre-gl");
        await import("maplibre-gl/dist/maplibre-gl.css");
        if (cancelled || !containerRef.current) return;

        const latest = latestLocationRef.current;
        const hasCoordinates =
          isCoordinate(latest.latitude) && isCoordinate(latest.longitude);
        const isFrance = (latest.country ?? "")
          .toLowerCase()
          .includes("france");
        const centre: [number, number] = hasCoordinates
          ? [latest.longitude, latest.latitude]
          : isFrance
            ? FRANCE_CENTRE
            : UK_CENTRE;

        const map = new maplibre.Map({
          container: containerRef.current,
          style: OSM_RASTER_STYLE,
          center: centre,
          zoom: hasCoordinates ? 14 : isFrance ? 5.4 : 5.2,
          attributionControl: false,
          touchPitch: false,
        });

        map.addControl(
          new maplibre.NavigationControl({ showCompass: false }),
          "top-right",
        );

        if (hasCoordinates) {
          markerRef.current = new maplibre.Marker().setLngLat(centre).addTo(map);
        }

        map.on("click", (event) => {
          const pickedLatitude = roundCoordinate(event.lngLat.lat);
          const pickedLongitude = roundCoordinate(event.lngLat.lng);

          if (!markerRef.current) {
            markerRef.current = new maplibre.Marker()
              .setLngLat([pickedLongitude, pickedLatitude])
              .addTo(map);
          } else {
            markerRef.current.setLngLat([pickedLongitude, pickedLatitude]);
          }

          onPickRef.current(pickedLatitude, pickedLongitude);
        });

        map.on("error", () => {
          if (!cancelled) {
            setLoadError(
              "Some map tiles could not load. You can retry, or enter latitude and longitude manually below.",
            );
          }
        });

        mapRef.current = map;
      } catch {
        if (!cancelled) {
          setLoadError(
            "The map could not load. You can still enter latitude and longitude manually below.",
          );
        }
      }
    }

    void initialiseMap();

    return () => {
      cancelled = true;
      markerRef.current?.remove();
      markerRef.current = null;
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !isCoordinate(latitude) || !isCoordinate(longitude)) {
      return;
    }

    const map = mapRef.current;
    const point: [number, number] = [longitude, latitude];

    void import("maplibre-gl").then((maplibre) => {
      if (!mapRef.current) return;
      if (!markerRef.current) {
        markerRef.current = new maplibre.Marker().setLngLat(point).addTo(map);
      } else {
        markerRef.current.setLngLat(point);
      }
      map.easeTo({
        center: point,
        zoom: Math.max(map.getZoom(), 14),
        duration: 450,
      });
    });
  }, [latitude, longitude]);

  useEffect(() => {
    if (
      !mapRef.current ||
      (isCoordinate(latitude) && isCoordinate(longitude))
    ) {
      return;
    }

    const isFrance = (country ?? "").toLowerCase().includes("france");
    if (!isFrance) return;

    mapRef.current.easeTo({
      center: FRANCE_CENTRE,
      zoom: 5.4,
      duration: 450,
    });
  }, [country, latitude, longitude]);

  async function searchPlace() {
    const query = placeQuery.trim();
    if (!query) {
      setPlaceSearchError("Enter a fishery, lake, town or nearby place first.");
      return;
    }

    const code = countryCode(country);
    const cacheKey = `${code ?? "all"}:${query.toLowerCase()}`;
    const cached = searchCacheRef.current.get(cacheKey);
    if (cached) {
      setPlaceResults(cached);
      setPlaceSearchError(cached.length ? null : "No matching places found.");
      return;
    }

    setSearchingPlace(true);
    setPlaceSearchError(null);
    try {
      const elapsed = Date.now() - lastSearchAtRef.current;
      if (elapsed < 1100) {
        await new Promise((resolve) => setTimeout(resolve, 1100 - elapsed));
      }

      const params = new URLSearchParams({
        format: "jsonv2",
        q: query,
        limit: "5",
        addressdetails: "1",
      });
      if (code) params.set("countrycodes", code);

      lastSearchAtRef.current = Date.now();
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params.toString()}`,
        {
          headers: {
            Accept: "application/json",
            "Accept-Language": "en",
          },
        },
      );
      if (!response.ok) throw new Error("Place search is temporarily unavailable.");

      const results = (await response.json()) as PlaceResult[];
      const safeResults = Array.isArray(results) ? results.slice(0, 5) : [];
      searchCacheRef.current.set(cacheKey, safeResults);
      setPlaceResults(safeResults);
      setPlaceSearchError(safeResults.length ? null : "No matching places found.");
    } catch (error) {
      setPlaceResults([]);
      setPlaceSearchError(
        error instanceof Error
          ? error.message
          : "Place search is temporarily unavailable.",
      );
    } finally {
      setSearchingPlace(false);
    }
  }

  function goToPlace(result: PlaceResult) {
    const resultLatitude = Number(result.lat);
    const resultLongitude = Number(result.lon);
    if (
      !mapRef.current ||
      !Number.isFinite(resultLatitude) ||
      !Number.isFinite(resultLongitude)
    ) {
      setPlaceSearchError("That search result did not include usable coordinates.");
      return;
    }

    mapRef.current.easeTo({
      center: [resultLongitude, resultLatitude],
      zoom: 14,
      duration: 650,
    });
    setPlaceResults([]);
    setPlaceSearchError(null);
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2 rounded-xl border border-border bg-inset p-3">
        <label htmlFor="lake-map-place-search" className="text-sm font-medium text-primary">
          Find the fishery or nearby place
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id="lake-map-place-search"
            type="search"
            value={placeQuery}
            onChange={(event) => setPlaceQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void searchPlace();
              }
            }}
            placeholder={
              (country ?? "").toLowerCase().includes("france")
                ? "e.g. Les Quis, Bray-sur-Seine"
                : "Fishery, lake, town or postcode"
            }
            className="min-h-11 w-full rounded-lg border border-border bg-raised px-3 py-2 text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => void searchPlace()}
            disabled={searchingPlace}
            className="min-h-11 shrink-0 rounded-lg bg-accent px-4 py-2 font-medium text-accent-fg disabled:cursor-wait disabled:opacity-60"
          >
            {searchingPlace ? "Searching…" : "Find place"}
          </button>
        </div>
        <p className="text-xs text-secondary">
          Search only moves the map. It does not save coordinates. Tap the actual
          centre of the water on the map to set the trusted CatchCompass location.
        </p>
        {placeSearchError && (
          <p className="text-sm text-warning" role="status">
            {placeSearchError}
          </p>
        )}
        {placeResults.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border bg-raised">
            {placeResults.map((result) => (
              <button
                key={result.place_id}
                type="button"
                onClick={() => goToPlace(result)}
                className="block w-full border-b border-border/60 px-3 py-3 text-left text-sm text-primary last:border-b-0 hover:bg-inset focus-visible:bg-inset focus-visible:outline-none"
              >
                {result.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        className="h-[360px] w-full overflow-hidden rounded-xl border border-border bg-inset"
        aria-label="Interactive map for choosing the lake centre"
      />
      <p className="text-xs text-secondary">
        Map and search data ©{" "}
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
          className="text-accent hover:underline"
        >
          OpenStreetMap contributors
        </a>
        .
      </p>
      {loadError ? (
        <p className="text-sm text-warning" role="status">
          {loadError}
        </p>
      ) : (
        <p className="text-xs text-secondary">
          Zoom and move the map, then tap or click a representative point near
          the centre of the water. CatchCompass stores only the latitude and
          longitude.
        </p>
      )}
    </div>
  );
}
